import { formatComment } from './utils';

// Go field names and types for the Link configurations used in the docs,
// checked against fern-demo/plaid-go commit 158af17a4196.
const fields: Record<string, [string, string]> = {
  access_token: ['AccessToken', '*string'],
  account_filters: ['AccountFilters', '*plaid.LinkTokenAccountFilters'],
  'account_filters.credit': ['Credit', '*plaid.CreditFilter'],
  'account_filters.credit.account_subtypes': [
    'AccountSubtypes',
    '[]plaid.CreditAccountSubtype',
  ],
  'account_filters.depository': ['Depository', '*plaid.DepositoryFilter'],
  'account_filters.depository.account_subtypes': [
    'AccountSubtypes',
    '[]plaid.DepositoryAccountSubtype',
  ],
  additional_consented_products: [
    'AdditionalConsentedProducts',
    '[]plaid.Products',
  ],
  auth: ['Auth', '*plaid.LinkTokenCreateRequestAuth'],
  'auth.automated_microdeposits_enabled': [
    'AutomatedMicrodepositsEnabled',
    '*bool',
  ],
  client_name: ['ClientName', 'string'],
  consumer_report_permissible_purpose: [
    'ConsumerReportPermissiblePurpose',
    '*plaid.ConsumerReportPermissiblePurpose',
  ],
  country_codes: ['CountryCodes', '[]plaid.CountryCode'],
  cra_options: ['CraOptions', '*plaid.LinkTokenCreateRequestCraOptions'],
  'cra_options.days_requested': ['DaysRequested', 'int'],
  enable_multi_item_link: ['EnableMultiItemLink', '*bool'],
  identity_verification: [
    'IdentityVerification',
    '*plaid.LinkTokenCreateRequestIdentityVerification',
  ],
  'identity_verification.template_id': ['TemplateID', 'string'],
  income_verification: [
    'IncomeVerification',
    '*plaid.LinkTokenCreateRequestIncomeVerification',
  ],
  'income_verification.bank_income': [
    'BankIncome',
    '*plaid.LinkTokenCreateRequestIncomeVerificationBankIncome',
  ],
  'income_verification.bank_income.days_requested': ['DaysRequested', 'int'],
  'income_verification.income_source_types': [
    'IncomeSourceTypes',
    '[]plaid.IncomeVerificationSourceType',
  ],
  'income_verification.payroll_income': [
    'PayrollIncome',
    '*plaid.LinkTokenCreateRequestIncomeVerificationPayrollIncome',
  ],
  'income_verification.payroll_income.flow_types': [
    'FlowTypes',
    '[]plaid.IncomeVerificationPayrollFlowType',
  ],
  language: ['Language', 'string'],
  payment_initiation: [
    'PaymentInitiation',
    '*plaid.LinkTokenCreateRequestPaymentInitiation',
  ],
  'payment_initiation.consent_id': ['ConsentID', '*string'],
  'payment_initiation.payment_id': ['PaymentID', '*string'],
  products: ['Products', '[]plaid.Products'],
  redirect_uri: ['RedirectURI', '*string'],
  required_if_supported_products: [
    'RequiredIfSupportedProducts',
    '[]plaid.Products',
  ],
  statements: ['Statements', '*plaid.LinkTokenCreateRequestStatements'],
  'statements.end_date': ['EndDate', 'time.Time'],
  'statements.start_date': ['StartDate', 'time.Time'],
  transactions: ['Transactions', '*plaid.LinkTokenTransactions'],
  'transactions.days_requested': ['DaysRequested', '*int'],
  update: ['Update', '*plaid.LinkTokenCreateRequestUpdate'],
  'update.account_selection_enabled': ['AccountSelectionEnabled', '*bool'],
  user: ['User', '*plaid.LinkTokenCreateRequestUser'],
  'user.address': ['Address', '*plaid.LinkTokenCreateRequestUserAddress'],
  'user.address.city': ['City', '*string'],
  'user.address.country': ['Country', 'string'],
  'user.address.postal_code': ['PostalCode', '*string'],
  'user.address.region': ['Region', '*string'],
  'user.address.street': ['Street', '*string'],
  'user.address.street2': ['Street2', '*string'],
  'user.client_user_id': ['ClientUserID', 'string'],
  'user.email_address': ['EmailAddress', '*string'],
  'user.legal_name': ['LegalName', '*string'],
  'user.name': ['Name', '*plaid.LinkTokenCreateRequestUserName'],
  'user.name.family_name': ['FamilyName', 'string'],
  'user.name.given_name': ['GivenName', 'string'],
  'user.phone_number': ['PhoneNumber', '*string'],
  user_id: ['UserID', '*string'],
  user_token: ['UserToken', '*string'],
  webhook: ['Webhook', '*string'],
};

const goLiteral = (
  value: unknown,
  type: string,
  path = '',
  depth = 0,
): string => {
  const indent = '  '.repeat(depth);
  if (type.startsWith('[]')) {
    return `${type}{${(value as unknown[])
      .map((item) => {
        return goLiteral(item, type.slice(2), path, depth);
      })
      .join(', ')}}`;
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value).map(([key, child]) => {
      const childPath = path ? `${path}.${key}` : key;
      const field = fields[childPath];
      if (!field) {
        throw new Error(`Missing new Go SDK field mapping: ${childPath}`);
      }
      const [name, childType] = field;
      return `${indent}  ${name}: ${goLiteral(
        child,
        childType,
        childPath,
        depth + 1,
      )},`;
    });
    const structType = type.replace(/^\*/, '');
    return `${type.startsWith('*') ? '&' : ''}${structType}{\n${entries.join(
      '\n',
    )}\n${indent}}`;
  }
  if (type.replace(/^\*/, '') === 'time.Time') {
    const [year, month, day] = String(value).split('-').map(Number);
    const date = `time.Date(${year}, ${month}, ${day}, 0, 0, 0, 0, time.UTC)`;
    return type.startsWith('*') ? `plaid.Time(${date})` : date;
  }
  const literal = JSON.stringify(value);
  if (!type.startsWith('*')) {
    return literal;
  }
  const scalarType = type.slice(1);
  const helper = {
    string: 'String',
    bool: 'Bool',
    int: 'Int',
    float64: 'Float64',
  }[scalarType];
  return helper
    ? `plaid.${helper}(${literal})`
    : `${scalarType}(${literal}).Ptr()`;
};

export const createFernGoSampleCode = (
  config: Record<string, unknown>,
  comment: string,
): string => {
  const request = goLiteral(config, '*plaid.LinkTokenCreateRequest');
  const imports = request.includes('time.Date(') ? 'import "time"\n\n' : '';
  return `${imports}${formatComment(comment, '// ')}request := ${request}

response, err := client.Link.CreateToken(ctx, request)
linkToken := response.LinkToken`;
};
